import React, { useEffect } from 'react';
import { SuccessState } from '@/components/SuccessState';
import { CounterOfferTab, useCounterOfferStore } from '@/stores/counterOfferStore';
import { PoolDetail } from '@/types/pool';
import { Modal } from '../../ui/Modal';
import { Tabs } from '../../ui/Tabs';
import { ModalTitle } from '../ModalTitle';
import { CounterOfferDocumentsStep } from './CounterOfferDocumentsStep';
import { CounterOfferNoteStep } from './CounterOfferNoteStep';
import { CounterOfferPoolTermsStep } from './CounterOfferPoolTermsStep';

type CounterOfferModalProps = {
  pool: PoolDetail;
  isOpen: boolean;
  onClose: () => void;
};

const tabs: { id: CounterOfferTab; label: string }[] = [
  { id: 'documents', label: 'Documents' },
  { id: 'poolTerms', label: 'Pool Terms' },
  { id: 'note', label: 'Add a note' },
];

export const CounterOfferModal: React.FC<CounterOfferModalProps> = ({ pool, isOpen, onClose }) => {
  const activeTab = useCounterOfferStore((state) => state.activeTab);
  const showSuccess = useCounterOfferStore((state) => state.showSuccess);
  const setActiveTab = useCounterOfferStore((state) => state.setActiveTab);
  const setShowSuccess = useCounterOfferStore((state) => state.setShowSuccess);
  const initializeFromPool = useCounterOfferStore((state) => state.initializeFromPool);
  const reset = useCounterOfferStore((state) => state.reset);

  // Initialize counter offer values with pool values
  useEffect(() => {
    if (isOpen) {
      initializeFromPool(
        pool.loan_terms.amount,
        pool.loan_terms.roi_rate,
        pool.loan_terms.loan_type,
        pool.loan_terms.term_months
      );
    }
  }, [isOpen, pool, initializeFromPool]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSuccessOk = () => {
    setShowSuccess(false);
    reset();
    onClose();
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setActiveTab('documents');
    }
  }, [isOpen, setShowSuccess, setActiveTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'documents':
        return <CounterOfferDocumentsStep pool={pool} />;
      case 'poolTerms':
        return <CounterOfferPoolTermsStep pool={pool} />;
      case 'note':
        return <CounterOfferNoteStep poolId={pool.id} />;
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={760} header={<ModalTitle title="Counter Offer" />}>
      {showSuccess ? (
        <SuccessState
          title="Counter Offer Sent"
          message="Your counter offer has been sent successfully. The borrower will review and respond within 72 hours."
          onButtonClick={handleSuccessOk}
        />
      ) : (
        <div className="flex flex-col h-full">
          {/* Tabs */}
          <div className="px-8 pt-6">
            <Tabs tabs={tabs} activeTab={activeTab} onTabChange={(tabId) => setActiveTab(tabId as CounterOfferTab)} />
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">{renderTabContent()}</div>
        </div>
      )}
    </Modal>
  );
};
